import React, { useEffect, useState } from "react";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Tooltip from '@mui/material/Tooltip';
import sum from 'lodash/sum';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import ResetIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import Button from '@mui/material/Button';

export default function InternalSimulator(props) {
  const {
    controlMeasures, 
    events, 
    impactCategories, 
    localFactors, 
    scores, 
    bonusPointGroups
  } = props;

  const [eventImpactCategories, setEventImpactCategories] = useState();
  const [eventLocalFactors, setEventLocalFactors] = useState();
  const [eventControlMeasures, setEventControlMeasures] = useState();

  const [eventImpactCategoriesDict, setEventImpactCategoriesDict] = useState();
  const [scoredEvents, setScoredEvents] = useState();
  const [score, setScore] = useState();
  const [impactScores, setImpactScores] = useState({});
  const [touched, setTouched] = useState();

  useEffect(() => {
    if (events) {
      const _eventImpactCategoriesDict = mapValues(keyBy(events, 'id'), v => v.impactCategories.split(',').filter(x => x.length));
      setEventImpactCategoriesDict(_eventImpactCategoriesDict);
    }
  }, [events]);

  useEffect(() => {
    if (events) {
      setScoredEvents(events.map(e => ({...e, controlStrength: 2.5, risk: 0.5, na: false})));

      const _eventImpactCategories = events.map(e => e.impactCategories.split(',').map(cm => ({
        eventId: e.id,
        impactCategoryId: cm
      }))).flat();
      setEventImpactCategories(_eventImpactCategories);

      const _eventLocalFactors = mapValues(keyBy(events, 'id'), v => v.localFactors.split(',').filter(x => x.length));
      setEventLocalFactors(_eventLocalFactors);

      const _eventControlMeasures = mapValues(keyBy(events, 'id'), v => v.controlMeasures.split(',').filter(x => x.length));
      setEventControlMeasures(_eventControlMeasures);
    }
  }, [events]);

  useEffect(() => {
    if (scoredEvents) {
      const _scoredEvents = scoredEvents.filter(e => !e.na);
      const eventScores = _scoredEvents.map(e => {
        const {controlStrength, risk, low, high} = e;
        const irp = Math.round(low + risk * (high - low));
        const csIdx = Math.round(controlStrength * 10);
        return {
          eventId: e.id,
          score: scores[irp][csIdx]
        };
      })

      const eventScoreDict = Object.assign({}, ...eventScores.map(es => ({[es.eventId]: es.score})));
      const impactScores = eventImpactCategories
        .filter(ic => eventScoreDict[ic.eventId] != null)
        .reduce((acc, cur) => {
          const catAcc = acc[cur.impactCategoryId] || {};
          catAcc.total = (catAcc.total || 0) + eventScoreDict[cur.eventId];
          catAcc.count = (catAcc.count || 0) + 1;
          catAcc.avg = catAcc.total / catAcc.count;
          acc[cur.impactCategoryId] = catAcc;
          return acc;
        }, {});
      setImpactScores(impactScores);

      const sumScores = eventScores.reduce((acc, cur) => acc + cur.score, 0);
      setScore(sumScores / _scoredEvents.length);
    }
  }, [scoredEvents, scores, eventImpactCategories]);

  const scoreLogo = () => {
    const baseUrl = 'https://projectcanarypublicimages.s3.us-east-2.amazonaws.com/trustwell_logos/';
    if (score > 124)
      return baseUrl + 'twpc-epwell-3platinum-300px.png';
    else if (score > 99)
      return baseUrl + 'twpc-epwell-2gold-300px.png';
    else if (score > 74)
      return baseUrl + 'twpc-epwell-1silver-300px.png';
    else
      return baseUrl + 'twpc-epwell-0evaluated-300px.png';
  };

  const handleRiskChange = (changeEvent, eventId) => {
    const _scoredEvents = [...scoredEvents];
    _scoredEvents.find(e => e.id === eventId).risk = +changeEvent.target.value;
    setScoredEvents(_scoredEvents);
    setTouched(true);
  }

  const handleCSChange = (changeEvent, eventId) => {
    const _scoredEvents = [...scoredEvents];
    _scoredEvents.find(e => e.id === eventId).controlStrength = +changeEvent.target.value;
    setScoredEvents(_scoredEvents);
    setTouched(true);
  }

  const handleNAChange = (changeEvent, eventId) => {
    const _scoredEvents = [...scoredEvents];
    _scoredEvents.find(e => e.id === eventId).na = changeEvent.target.checked;
    setScoredEvents(_scoredEvents);
    setTouched(true);
  }

  const resetDefaults = () => {
    setScoredEvents(events.map(e => ({...e, controlStrength: 2.5, risk: 0.5, na: false})));
    setTouched(false);
  }

  const getEventTooltip = (id) => {
    if (!eventLocalFactors && eventControlMeasures) return;
    const _localFactors = eventLocalFactors[id].map(elf => localFactors[elf]);
    const _controlMeasures = eventControlMeasures[id].map(ecm => controlMeasures[ecm]);
    const _impactCategories = eventImpactCategoriesDict[id].map(eic => impactCategories[eic]);

    if (!_localFactors.length && !_controlMeasures.length && !_impactCategories.length) {
      return '';
    }
    return (
      <Box>
        {_impactCategories.length ? (
          <>
            <Typography>Primary Categories:</Typography>
            <ul style={{marginTop: 0}}>
              {_impactCategories.map(ic => <li key={ic}>{ic}</li>)}
            </ul>
          </>
        ) : ''}
        {_localFactors.length ? (
          <>
            <Typography>Local Factors:</Typography>
            <ul style={{marginTop: 0}}>
              {_localFactors.map(lf => <li key={lf}>{lf}</li>)}
            </ul>
          </>
        ) : ''}
        {_controlMeasures.length ? (
          <>
            <Typography>Control Measures:</Typography>
            <ul style={{marginTop: 0}}>
              {_controlMeasures.map(cm => <li key={cm.name}>{cm.name} ({cm.questions} Questions)</li>)}
            </ul>
          </>
        ) : ''}
      </Box>
    );
  }

  return (
    <Paper sx={{height: '100%'}}>
      <Stack sx={{height: '100%'}}>
        <Stack 
          direction="row"
          justifyContent="space-between" 
          alignItems="center"
          sx={{width: '1060px', margin: '10px auto'}}
        >
          <img src={scoreLogo()} style={{width: '120px', height: '120px'}} alt="" />
          <Typography variant="h2">Score: {score?.toFixed(1)}</Typography>

          <Stack p={1} sx={{width: '180px'}}>
            {Object.entries(impactScores).map(kv => (
              <Stack direction="row" key={`impact-category-${kv[0]}`} justifyContent="space-between">
                <Typography>{impactCategories[+kv[0]]}:</Typography>
                <Typography>{kv[1].avg.toFixed(1)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        <Divider />

        <Box flex={1} sx={{overflowY: 'auto'}}>
          <Stack sx={{width: '800px', margin: '0 auto'}} justifyContent="center">

            <Stack direction="row">
              <Box flex={2}>
                <Stack direction="row" py={2} ml={0.5}>
                  <Typography variant="h5" sx={{width: '286px'}}>Event</Typography>
                  <Typography variant="h5" sx={{width: '214px'}}>Risk Profile</Typography>
                  <Typography variant="h5" sx={{width: '210px'}}>Control Strength</Typography>
                  {touched && <Button size="small" startIcon={<ResetIcon />} onClick={resetDefaults}>Reset</Button>}
                </Stack>

                {scoredEvents && scoredEvents.map(se => (
                  <Stack direction="row" key={se.name} sx={{width: '800px'}} py={0.5} >

                    <Stack direction="row" sx={{minWidth: '280px', width: '280px'}} alignItems="center">

                      <Tooltip title={getEventTooltip(se.id)}>
                        <InfoIcon fontSize="small" sx={{cursor: 'help'}} />
                      </Tooltip>

                      <Typography variant="body1" 
                          sx={{
                            lineHeight: '42px', 
                            textDecoration: se.na ? 'line-through' : 'none',
                            color: se.na ? 'text.secondary' : 'text.primary',
                            marginLeft: '8px'
                          }}
                        >
                          {se.name}
                        </Typography>
                    </Stack>

                    <Box flexGrow={1} px={1} mr={1}>
                      <Slider 
                        min={0}
                        max={1}
                        step={0.01}
                        valueLabelDisplay="auto"
                        valueLabelFormat={v => Math.round(se.low + v * (se.high - se.low))}
                        value={se.risk} 
                        onChange={e => handleRiskChange(e, se.id)} 
                        disabled={se.na}
                      />
                    </Box>

                    <Box flexGrow={1} px={1}>
                      <Slider 
                        min={0}
                        max={5}
                        step={0.1}
                        valueLabelDisplay="auto"
                        value={se.controlStrength} 
                        onChange={e => handleCSChange(e, se.id)} 
                        disabled={se.na}
                      />
                    </Box>
                    
                    <Box px={1}>
                      <FormControlLabel control={<Checkbox checked={se.na} onChange={e => handleNAChange(e, se.id)} />} label="N/A" />
                    </Box>
                  </Stack>
                ))}
              </Box>
            </Stack>

          </Stack>
        </Box>

      </Stack>
    </Paper>
  )
}